import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { BadgeCheck, LucideAngularModule } from 'lucide-angular';
import { Solution } from '../../../solutions/models/solutions.types';
import { formatChemicalUnit } from '../../../../shared/utils/format-unit';

interface Cell {
  value: string | number | null | undefined;
  unit?: string;
}
interface Row {
  key: string;
  label: string;
  getValue: (s: Solution) => Cell | null;
  perUnitFrom?: string;
}

interface DisplayRow extends Row {
  rowUnit?: string;
}

interface Section {
  title: string;
  rows: Row[];
}

interface DisplaySection {
  title: string;
  rows: DisplayRow[];
}

@Component({
  selector: 'app-comparison-table',
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './comparison-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComparisonTableComponent {
  readonly solutions = input.required<Solution[]>();
  readonly badge = BadgeCheck;

  private property(label: string): (solution: Solution) => Cell | null {
    return (solution: Solution) => {
      const property = solution.properties.find(p => p.label === label);
      return property ? { value: property.value, unit: property.unit } : null;
    };
  }

  private hasValue(value: unknown): boolean {
    return value !== null && value !== undefined && value !== '' && value !== 0;
  }

  //don’t show rows that are empty for all compared solutions
  private rowHasAnyData(row: Row, solutions: Solution[]): boolean {
    return solutions.some(s => {
      const cell = row.getValue(s);
      return !!cell && this.hasValue(cell.value);
    });
  }

  private formatValue(value: string | number): string {
    if (typeof value === 'number') {
      return new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 2,
      }).format(value);
    }
    return value;
  }

  private getRowUnit(row: Row, solutions: Solution[]): string | undefined {
    for (const solution of solutions) {
      const cell = row.getValue(solution);
      if (cell?.unit) {
        return cell.unit;
      }
    }
    return undefined;
  }

  displayCell(cell: Cell | null): { text: string } {
    if (!cell || !this.hasValue(cell.value)) {
      return { text: '—' };
    }
    return { text: this.formatValue(cell.value as string | number) };
  }

  private readonly sectionConfig: Section[] = [
    {
      title: 'Fuel',
      rows: [
        {
          key: 'fuel.type',
          label: 'Type of fuel',
          getValue: solution => ({ value: solution.dieselType }),
        },
        {
          key: 'fuel.costPerLiter',
          label: 'Cost per liter',
          getValue: this.property('Diesel cost per liter'),
          perUnitFrom: 'fuel.consumption',
        },
        {
          key: 'fuel.totalCost',
          label: 'Total fuel cost',
          getValue: this.property('Diesel cost'),
        },
        {
          key: 'fuel.consumption',
          label: 'Fuel consumption',
          getValue: this.property('Diesel consumption'),
        },
        {
          key: 'fuel.co2',
          label: 'CO₂e emissions',
          getValue: this.property('Diesel CO₂ emissions'),
        },
      ],
    },
    {
      title: 'Natural gas',
      rows: [
        {
          key: 'ng.consumption',
          label: 'Natural gas consumption',
          getValue: this.property('Natural gas consumption'),
        },
        {
          key: 'ng.costPerLiter',
          label: 'Cost per liter',
          getValue: this.property('Natural gas cost per liter'),
          perUnitFrom: 'ng.consumption',
        },
        {
          key: 'ng.totalCost',
          label: 'Total cost natural gas',
          getValue: this.property('Natural gas cost'),
        },
        {
          key: 'ng.co2',
          label: 'CO₂e emissions',
          getValue: this.property('Natural gas CO₂ emissions'),
        },
        {
          key: 'ng.maxPower',
          label: 'Maximum net power',
          getValue: this.property('Natural gas max power'),
        },
      ],
    },
    {
      title: 'Electricity',
      rows: [
        {
          key: 'el.kwh',
          label: 'Electricity consumption',
          getValue: this.property('Electricity consumption'),
        },
        {
          key: 'el.costPerKw',
          label: 'Cost per kWh',
          getValue: this.property('Electricity cost (EUR)'),
          perUnitFrom: 'el.kwh',
        },
        {
          key: 'el.totalCost',
          label: 'Total cost electricity',
          getValue: this.property('Electricity cost'),
        },
        {
          key: 'el.co2',
          label: 'CO₂e emissions',
          getValue: this.property('Electricity CO₂ emissions'),
        },
      ],
    },
    {
      title: 'Emissions',
      rows: [
        {
          key: 'emissions.co2Total',
          label: 'Total CO₂e emissions',
          getValue: this.property('Total CO₂ emissions'),
        },
        {
          key: 'emissions.carbonCost',
          label: 'Cost of carbon',
          getValue: this.property('Carbon cost'),
        },
        {
          key: 'emissions.nox',
          label: 'NOₓ emissions',
          getValue: this.property('NOx emissions'),
        },
        {
          key: 'emissions.pm',
          label: 'PM emissions',
          getValue: this.property('PM emissions'),
        },
      ],
    },
    {
      title: 'Total costs',
      rows: [
        {
          key: 'total.energyCost',
          label: 'Energy cost',
          getValue: s => ({
            value: s.kpis.energyCostTotal ?? null,
            unit: s.kpis.currencyUnit,
          }),
        },
        {
          key: 'total.rentalFee',
          label: 'Rental fee',
          getValue: s => ({
            value: s.kpis.rentalFeeTotal ?? null,
            unit: s.kpis.currencyUnit,
          }),
        },
        {
          key: 'total.tco',
          label: 'Total cost of usership',
          getValue: s => ({
            value: (s.kpis.energyCostTotal ?? 0) + (s.kpis.rentalFeeTotal ?? 0),
            unit: s.kpis.currencyUnit,
          }),
        },
      ],
    },
  ];

  readonly visibleSections = computed<DisplaySection[]>(() => {
    const solutions = this.solutions();
    return this.sectionConfig
      .map(section => ({
        ...section,
        rows: section.rows
          .filter(r => this.rowHasAnyData(r, solutions))
          .map((row): DisplayRow => {
            let unit = this.getRowUnit(row, solutions);
            if (row.perUnitFrom && unit && !unit.includes('/')) {
              const denomRow = section.rows.find(r => r.key === row.perUnitFrom);
              const denomUnit = denomRow ? this.getRowUnit(denomRow, solutions) : undefined;
              if (denomUnit) {
                unit = `${unit}/${denomUnit}`;
              }
            }
            return {
              ...row,
              rowUnit: formatChemicalUnit(unit),
            };
          }),
      }))
      .filter(sec => sec.rows.length > 0);
  });

  readonly gridTemplateColumns = computed(() => {
    const n = this.solutions().length;
    return `minmax(240px, 1.4fr) repeat(${n}, minmax(120px, 1fr))`;
  });
}
